import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  DollarSign,
  Send,
  Calculator,
  PieChart,
  Megaphone,
  Headphones,
  Zap,
  ShieldCheck,
  Coins,
  Layers
} from "lucide-react";

const useStyles = makeStyles((theme) => ({
  card: {
    height: "100%",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 18px rgba(0,0,0,0.08)"
    }
  },
  cardContent: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2.5),
    "&:last-child": {
      paddingBottom: theme.spacing(2.5)
    }
  },
  iconBox: {
    width: 48,
    height: 48,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(2),
    flexShrink: 0
  },
  value: {
    fontWeight: 700,
    fontSize: "1.5rem",
    lineHeight: 1.2,
    color: theme.palette.text.primary
  },
  title: {
    fontSize: "0.825rem",
    color: theme.palette.text.secondary,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(0.5)
  },
  subtitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  // Seção de KPIs por categoria
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  sectionTitle: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: theme.palette.text.primary
  },
  sectionSubtitle: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary
  },
  categoryCard: {
    height: "100%",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(0,0,0,0.08)"
    }
  },
  categoryCardContent: {
    padding: theme.spacing(2),
    "&:last-child": {
      paddingBottom: theme.spacing(2)
    }
  },
  categoryIconBox: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1.2),
    flexShrink: 0
  },
  categoryName: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.text.primary
  },
  categoryCost: {
    fontSize: "1.35rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  categoryStats: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  progressBarBg: {
    width: "100%",
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.palette.divider,
    overflow: "hidden",
    marginTop: theme.spacing(1.5)
  },
  progressBarFill: {
    height: "100%",
    borderRadius: 2,
    transition: "width 0.4s ease"
  }
}));

const formatCurrency = (val, currency = "USD", decimals = 2) => {
  const num = Number(val || 0);
  const fractionDigits = num > 0 && num < 0.01 ? 4 : decimals;
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: fractionDigits
    }).format(num);
  } catch (e) {
    return `${currency} ${num.toFixed(fractionDigits)}`;
  }
};

const formatNumber = (val) => {
  return new Intl.NumberFormat("pt-BR").format(Number(val || 0));
};

const KpiCards = ({ summary }) => {
  const classes = useStyles();

  const totalCost = summary?.totalCost || 0;
  const totalVolume = summary?.totalVolume || 0;
  const averageCost = summary?.averageCostPerMessage || 0;
  const currency = summary?.currency || "USD";

  // Identifica a categoria com maior volume
  const categories = summary?.byCategory || {};
  let topCategory = "N/A";
  let maxCatVolume = 0;
  Object.entries(categories).forEach(([key, val]) => {
    if (val.volume > maxCatVolume) {
      maxCatVolume = val.volume;
      topCategory = key;
    }
  });

  const categoryLabels = {
    MARKETING: "Marketing",
    UTILITY: "Utilidade",
    AUTHENTICATION: "Autenticação",
    SERVICE: "Serviço",
    OTHER: "Outros"
  };

  const cardsData = [
    {
      title: "Total Gasto",
      value: formatCurrency(totalCost, currency),
      subtitle: `Moeda: ${currency}`,
      icon: DollarSign,
      color: "#10b981",
      bgColor: "rgba(16, 185, 129, 0.12)"
    },
    {
      title: "Mensagens Enviadas",
      value: formatNumber(totalVolume),
      subtitle: "Total faturado pela Meta",
      icon: Send,
      color: "#3b82f6",
      bgColor: "rgba(59, 130, 246, 0.12)"
    },
    {
      title: "Custo Médio / Msg",
      value: formatCurrency(averageCost, currency),
      subtitle: "Média por mensagem faturada",
      icon: Calculator,
      color: "#8b5cf6",
      bgColor: "rgba(139, 92, 246, 0.12)"
    },
    {
      title: "Maior Categoria",
      value: categoryLabels[topCategory] || topCategory,
      subtitle: maxCatVolume > 0 ? `${formatNumber(maxCatVolume)} mensagens` : "Sem disparos",
      icon: PieChart,
      color: "#f59e0b",
      bgColor: "rgba(245, 158, 11, 0.12)"
    }
  ];

  // Configurações das categorias para os novos KPIs de valores gastos
  const categoryConfigs = [
    {
      key: "MARKETING",
      label: "Marketing",
      icon: Megaphone,
      color: "#2563eb",
      lightBg: "rgba(37, 99, 235, 0.12)",
      borderColor: "#2563eb"
    },
    {
      key: "SERVICE",
      label: "Serviço",
      icon: Headphones,
      color: "#10b981",
      lightBg: "rgba(16, 185, 129, 0.12)",
      borderColor: "#10b981"
    },
    {
      key: "UTILITY",
      label: "Utilidade",
      icon: Zap,
      color: "#f59e0b",
      lightBg: "rgba(245, 158, 11, 0.12)",
      borderColor: "#f59e0b"
    },
    {
      key: "AUTHENTICATION",
      label: "Autenticação",
      icon: ShieldCheck,
      color: "#8b5cf6",
      lightBg: "rgba(139, 92, 246, 0.12)",
      borderColor: "#8b5cf6"
    }
  ];

  // Se houver categoria 'OTHER' com custo ou volume > 0, adiciona também
  if (categories?.OTHER && (categories.OTHER.cost > 0 || categories.OTHER.volume > 0)) {
    categoryConfigs.push({
      key: "OTHER",
      label: "Outros",
      icon: Coins,
      color: "#6b7280",
      lightBg: "rgba(107, 114, 128, 0.12)",
      borderColor: "#6b7280"
    });
  }

  return (
    <Box>
      {/* 1. KPIs Gerais */}
      <Grid container spacing={2}>
        {cardsData.map((card, idx) => {
          const IconComponent = card.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card className={classes.card} elevation={0}>
                <CardContent className={classes.cardContent}>
                  <Box
                    className={classes.iconBox}
                    style={{ backgroundColor: card.bgColor, color: card.color }}
                  >
                    <IconComponent size={24} />
                  </Box>
                  <Box flex={1} overflow="hidden">
                    <Typography className={classes.title} noWrap>
                      {card.title}
                    </Typography>
                    <Typography className={classes.value} noWrap>
                      {card.value}
                    </Typography>
                    <Typography className={classes.subtitle} noWrap>
                      {card.subtitle}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* 2. Cabeçalho dos KPIs de Gastos por Categoria */}
      <Box className={classes.sectionHeader}>
        <Layers size={18} color="#2563eb" />
        <Typography className={classes.sectionTitle}>
          Gastos por Categoria
        </Typography>
        <Typography className={classes.sectionSubtitle}>
          (Valores faturados e distribuição por tipo de conversa)
        </Typography>
      </Box>

      {/* 3. Cards de KPIs de Gastos por Categoria */}
      <Grid container spacing={2}>
        {categoryConfigs.map((cat) => {
          const catData = categories[cat.key] || { cost: 0, volume: 0 };
          const catCost = Number(catData.cost || 0);
          const catVolume = Number(catData.volume || 0);
          const catPercentNum = totalCost > 0 ? (catCost / totalCost) * 100 : 0;
          const catPercent = catPercentNum.toFixed(1);
          const avgCatCost = catVolume > 0 ? catCost / catVolume : 0;
          const IconComponent = cat.icon;

          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={categoryConfigs.length > 4 ? 12 / categoryConfigs.length : 3}
              key={cat.key}
            >
              <Card
                className={classes.categoryCard}
                elevation={0}
                style={{ borderTop: `3px solid ${cat.borderColor}` }}
              >
                <CardContent className={classes.categoryCardContent}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box display="flex" alignItems="center">
                      <Box
                        className={classes.categoryIconBox}
                        style={{
                          backgroundColor: cat.lightBg,
                          color: cat.color
                        }}
                      >
                        <IconComponent size={18} />
                      </Box>
                      <Typography className={classes.categoryName}>
                        {cat.label}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${catPercent}%`}
                      size="small"
                      style={{
                        backgroundColor: cat.lightBg,
                        color: cat.color,
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        height: 22
                      }}
                    />
                  </Box>

                  <Typography className={classes.categoryCost}>
                    {formatCurrency(catCost, currency)}
                  </Typography>

                  <Box className={classes.categoryStats}>
                    <span>{formatNumber(catVolume)} msgs</span>
                    <span>
                      {catVolume > 0
                        ? `${formatCurrency(avgCatCost, currency)}/msg`
                        : "Sem custo/msg"}
                    </span>
                  </Box>

                  {/* Barra de Progresso do Percentual */}
                  <Box className={classes.progressBarBg}>
                    <Box
                      className={classes.progressBarFill}
                      style={{
                        width: `${Math.min(catPercentNum, 100)}%`,
                        backgroundColor: cat.color
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default KpiCards;
